import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const CommissionPreview = ({ onSubmit, onPrevious, isLoading, agentData }) => {
  const [performanceTargets, setPerformanceTargets] = useState({
    monthlyTarget: agentData?.performanceTargets?.monthlyTarget || '',
    yearlyTarget: agentData?.performanceTargets?.yearlyTarget || '',
    customerTarget: agentData?.performanceTargets?.customerTarget || '',
    acceptTargets: agentData?.performanceTargets?.acceptTargets || false
  });

  const [errors, setErrors] = useState({});

  const monthlyTargetOptions = [
    { value: '25000', label: '$25,000/month' },
    { value: '50000', label: '$50,000/month' },
    { value: '100000', label: '$100,000/month' },
    { value: '250000', label: '$250,000/month' },
    { value: '500000', label: '$500,000+/month' }
  ];

  const customerTargetOptions = [
    { value: '50', label: '50 customers/month' },
    { value: '100', label: '100 customers/month' },
    { value: '250', label: '250 customers/month' },
    { value: '500', label: '500+ customers/month' }
  ];

  // Calculate commission structure based on agent data
  const getCommissionStructure = () => {
    const baseCommission = {
      individual: 2.5,
      business: 3.0,
      franchise: 3.5
    };

    const territoryBonus = agentData?.territories?.includes('africa') ? 1.0 :
                          agentData?.territories?.includes('middle-east') ? 0.7 :
                          agentData?.territories?.includes('europe') ? 0.5 : 0.3;

    const volumeBonus = {
      '25000': 0,
      '50000': 0.2,
      '100000': 0.4,
      '250000': 0.6,
      '500000': 1.0
    };

    const base = baseCommission?.[agentData?.agentType] || 2.5;
    const territory = territoryBonus;
    const volume = volumeBonus?.[performanceTargets?.monthlyTarget] || 0;

    return {
      base: base,
      territoryBonus: territory,
      volumeBonus: volume,
      total: Math.min(base + territory + volume, 5.0) // Cap at 5%
    };
  };

  const commissionStructure = getCommissionStructure();

  const handleInputChange = (field, value) => {
    setPerformanceTargets(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user makes a selection
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!performanceTargets?.monthlyTarget) {
      newErrors.monthlyTarget = 'Please select your monthly volume target';
    }

    if (!performanceTargets?.customerTarget) {
      newErrors.customerTarget = 'Please select your customer acquisition target';
    }

    if (!performanceTargets?.acceptTargets) {
      newErrors.acceptTargets = 'You must agree to the performance targets';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      const yearlyTarget = performanceTargets?.monthlyTarget ? 
        (parseInt(performanceTargets?.monthlyTarget) * 12)?.toString() : '';
      
      const finalTargets = {
        ...performanceTargets,
        yearlyTarget,
        commissionStructure
      };
      
      onSubmit({ performanceTargets: finalTargets });
    }
  };

  const getEstimatedEarnings = () => {
    const monthlyVolume = parseInt(performanceTargets?.monthlyTarget) || 0;
    const commission = commissionStructure?.total / 100;
    const monthlyEarnings = monthlyVolume * commission;
    const yearlyEarnings = monthlyEarnings * 12;

    return {
      monthly: monthlyEarnings,
      yearly: yearlyEarnings
    };
  };

  const estimatedEarnings = getEstimatedEarnings();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center space-y-2 mb-6">
        <h2 className="text-2xl font-semibold text-foreground">Commission Structure & Targets</h2>
        <p className="text-muted-foreground">
          Review your commission rates and set performance targets to maximize your earning potential.
        </p>
      </div>

      {/* Commission Breakdown */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="TrendingUp" size={20} />
          <span>Your Commission Structure</span>
        </h3>

        <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg border border-primary/20 p-6">
          <div className="space-y-4">
            {/* Commission Components */}
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-primary/10">
                <span className="text-sm text-muted-foreground">Base Commission ({agentData?.agentType}):</span>
                <span className="font-medium text-foreground">{commissionStructure?.base?.toFixed(1)}%</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-primary/10">
                <span className="text-sm text-muted-foreground">Territory Bonus:</span>
                <span className="font-medium text-success">+{commissionStructure?.territoryBonus?.toFixed(1)}%</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-primary/10">
                <span className="text-sm text-muted-foreground">Volume Bonus:</span>
                <span className="font-medium text-success">+{commissionStructure?.volumeBonus?.toFixed(1)}%</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-2 border-primary/20 rounded bg-primary/5">
                <span className="font-semibold text-foreground">Total Commission Rate:</span>
                <span className="text-xl font-bold text-primary">{commissionStructure?.total?.toFixed(1)}%</span>
              </div>
            </div>

            {/* Additional Benefits */}
            <div className="mt-4 pt-4 border-t border-primary/20">
              <h4 className="font-medium text-foreground mb-2">Additional Benefits</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success" />
                  <span>Weekly commission payouts</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success" />
                  <span>Performance bonuses up to 2x commission</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success" />
                  <span>Quarterly recognition rewards</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success" />
                  <span>Annual conference and training</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Targets */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Target" size={20} />
          <span>Performance Targets</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Monthly Volume Target"
            placeholder="Select your target"
            options={monthlyTargetOptions}
            value={performanceTargets?.monthlyTarget}
            onChange={(value) => handleInputChange('monthlyTarget', value)}
            error={errors?.monthlyTarget}
            description="Minimum monthly transaction volume commitment"
            required
          />

          <Select
            label="Customer Acquisition Target"
            placeholder="Select customer target"
            options={customerTargetOptions}
            value={performanceTargets?.customerTarget}
            onChange={(value) => handleInputChange('customerTarget', value)}
            error={errors?.customerTarget}
            description="New customers to onboard monthly"
            required
          />
        </div>

        {performanceTargets?.monthlyTarget && performanceTargets?.customerTarget && (
          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3">Target Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Monthly Volume:</span>
                <p className="font-medium text-foreground">
                  ${parseInt(performanceTargets?.monthlyTarget)?.toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Yearly Volume:</span>
                <p className="font-medium text-foreground">
                  ${(parseInt(performanceTargets?.monthlyTarget) * 12)?.toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Monthly Customers:</span>
                <p className="font-medium text-foreground">
                  {performanceTargets?.customerTarget} new customers
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Yearly Customers:</span>
                <p className="font-medium text-foreground">
                  {parseInt(performanceTargets?.customerTarget) * 12} new customers
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Earnings Projection */}
      {performanceTargets?.monthlyTarget && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="DollarSign" size={20} />
            <span>Earnings Projection</span>
          </h3>

          <div className="bg-success/5 rounded-lg border border-success/20 p-6">
            <div className="text-center space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Estimated Monthly Earnings</p>
                <p className="text-3xl font-bold text-success">
                  ${estimatedEarnings?.monthly?.toLocaleString()}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground mb-1">Estimated Yearly Earnings</p>
                <p className="text-2xl font-bold text-foreground">
                  ${estimatedEarnings?.yearly?.toLocaleString()}
                </p>
              </div>

              <div className="text-xs text-muted-foreground">
                <p>* Projections based on achieving your monthly volume target</p>
                <p>* Additional performance bonuses not included</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Performance Agreement */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="FileSignature" size={20} />
          <span>Performance Agreement</span>
        </h3>

        <div className="bg-muted/30 rounded-lg p-4 space-y-3">
          <Checkbox
            label="I agree to the performance targets and commission structure"
            description="By accepting, you commit to achieving the selected targets and understand that commission rates are based on performance metrics"
            checked={performanceTargets?.acceptTargets}
            onChange={(e) => handleInputChange('acceptTargets', e?.target?.checked)}
            error={errors?.acceptTargets}
            required
          />

          <div className="text-sm text-muted-foreground space-y-2">
            <p><strong>Performance Review:</strong> Targets are reviewed quarterly with opportunity for adjustment based on market conditions.</p>
            <p><strong>Commission Calculation:</strong> Commissions are calculated based on net transaction volume and paid weekly.</p>
            <p><strong>Target Flexibility:</strong> Targets can be revised during your first 90 days as you establish your business.</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 pt-6">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="sm:w-auto"
        >
          Back to Territory Selection
        </Button>
        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isLoading}
          iconName="ArrowRight"
          iconPosition="right"
          className="sm:flex-1"
        >
          Continue to Final Review
        </Button>
      </div>
    </form>
  );
};

export default CommissionPreview;